import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../lib/api';
import orchestrator from '../lib/requestOrchestrator';
import CornerPanel from '../components/CornerPanel';
import RefreshButton from '../components/RefreshButton';
import NoTradeBanner from '../components/NoTradeBanner';
import ConfluenceGauge from '../components/ConfluenceGauge';
import TerminalLog from '../components/TerminalLog';
import TradingViewWidget from '../components/TradingViewWidget';
import useVisibilityAwareInterval from '../hooks/useVisibilityAwareInterval';
import useEventStream from '../hooks/useEventStream';
import PanelStatus from '../components/PanelStatus';
import watchlistStore from '../store/watchlistSnapshotStore';
import RegimeGrid from '../components/dashboard/RegimeGrid';
import { ChevronRight, RefreshCw, Search, Star, X } from 'lucide-react';
import { toast } from 'sonner';
import { pollJob } from '../lib/pollJob';

function formatPct(v) {
  if (v === null || v === undefined) return '—';
  const sign = v >= 0 ? '+' : '';
  return `${sign}${Number(v).toFixed(2)}%`;
}

function SignalPill({ signal, testId }) {
  const map = {
    BUY: 'text-buy border-buy',
    SELL: 'text-sell border-sell',
    HOLD: 'text-hold border-hold',
    WAIT: 'text-hold border-hold',
  };
  return (
    <span
      data-testid={testId}
      className={`px-2 py-0.5 border bg-black font-display tracking-[0.25em] text-[11px] ${map[signal] || 'text-cyan-sys border-cyan-sys'}`}
    >
      {signal}
    </span>
  );
}

function QualityMetric({ label, value, suffix = '', testId }) {
  // 0-100 metrics — color by band.
  const v = value;
  const color =
    v == null ? 'text-[var(--txt-muted)]' :
    v >= 70 ? 'text-buy' :
    v >= 40 ? 'text-hold' : 'text-sell';
  const bar = v == null ? 0 : Math.max(0, Math.min(100, v));
  const barColor =
    v == null ? 'bg-[var(--border-default)]' :
    v >= 70 ? 'bg-buy' : v >= 40 ? 'bg-hold' : 'bg-sell';
  return (
    <div data-testid={testId} className="space-y-1">
      <div className="text-[10px] uppercase tracking-wider text-cyan-sys">{label}</div>
      <div className={`text-2xl live-num ${color}`}>
        {v == null ? '—' : `${v}${suffix}`}
      </div>
      <div className="h-[3px] bg-[var(--border-default)] relative overflow-hidden">
        <div className={`absolute top-0 left-0 h-full ${barColor} transition-all duration-500`} style={{ width: `${bar}%` }} />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [symbol, setSymbol] = useState('AAPL');
  const [input, setInput] = useState('AAPL');
  const [decision, setDecision] = useState(null);
  const [loadingDec, setLoadingDec] = useState(false);
  const [watchlist, setWatchlist] = useState(() => watchlistStore.getSymbols() || []);
  // Phase 36.5.B — initialise quotes state from the persistent store so
  // a tab return shows prior prices immediately (before /market/batch resolves).
  const [quotes, setQuotes] = useState(() => watchlistStore.getQuotes() || {});
  // Phase 14 §9D: last-known-value memory for fallback display
  const lastKnownQuotesRef = useRef({ ...(watchlistStore.getQuotes() || {}) });
  // Phase 29.2 §B: per-symbol "pending hydration" set — drives Loading vs — state
  const [pendingHydration, setPendingHydration] = useState({});
  const [events, setEvents] = useState([]);
  const [freshness, setFreshness] = useState({ rows: [], stale_count: 0, any_stale: false });
  const [lastRefresh, setLastRefresh] = useState(null);
  // Phase 36.A / 36.5.B — Canonical watchlist snapshot (cross-mount store
  // preserves last-known rows so tab switches never blank a hydrated row).
  const [watchSnapshot, setWatchSnapshot] = useState(() => ({
    rows: watchlistStore.getRows(),
    bySymbol: watchlistStore.snapshot().bySymbol,
    quotes: watchlistStore.getQuotes(),
  }));
  // Phase 36.D — Trading engine health pill
  const [tradingHealth, setTradingHealth] = useState(null);

  const loadWatchSnapshot = async () => {
    await watchlistStore.refresh();
  };
  const loadTradingHealth = async () => {
    try {
      const { data } = await api.get('/trading/health');
      setTradingHealth(data);
    } catch {}
  };

  // Only reload the watchlist symbol list on mount / explicit refresh.
  // Periodic polls just refresh the quotes & freshness — keeps the table stable.
  const loadWatchlistSymbols = async () => {
    const { data } = await api.get('/watchlist');
    const syms = data.symbols || [];
    setWatchlist(syms);
    watchlistStore.setSymbols(syms);
    return syms;
  };

  const refreshQuotes = async (syms, forceQuotes = false) => {
    if (!syms || !syms.length) return;
    try {
      const q = forceQuotes ? '&force=true' : '';
      const qRes = await api.get(`/market/batch?symbols=${syms.join(',')}${q}`);
      const incoming = qRes.data.quotes || {};
      // Merge + filter: preserves last-known values if batch returns partial
      // / empty (rate-limit, cold-start, transient error). Also drops any
      // incoming quote with c<=0 so a "backend just woke up" zero-read never
      // wipes the real price.
      setQuotes((prev) => {
        const next = { ...prev };
        for (const [s, v] of Object.entries(incoming)) {
          if (v && typeof v.c === 'number' && v.c > 0) {
            next[s] = v;
            // Phase 36.5.B — persist into module-scoped store so a tab
            // return restores the price instantly without a refetch race.
            watchlistStore.setQuote(s, v);
          }
        }
        return next;
      });
      try {
        const fRes = await api.get(`/market/freshness?symbols=${syms.join(',')}`);
        if (fRes?.data) setFreshness(fRes.data);
      } catch {}
    } catch {
      // swallow — the UI keeps displaying previous values
    }
  };

  const loadDecision = async (sym) => {
    setLoadingDec(true);
    try {
      // Phase 34.A/B — route through orchestrator: HIGH priority, 5s TTL
      // dedupe so rapid clicks on the same symbol coalesce into a single
      // backend call. Cache invalidated via refreshAll(force).
      // Phase 51.M+ — bumped per-call timeout to 20s. The default 8s was
      // routinely aborting production requests when the L3 tier ran a
      // fresh Claude reasoning pass (which can take 10-15s), and the
      // aborted request was nulling `decision`, blanking all three
      // Command-tab panels (AI Decision / Firewall+Validation /
      // Reasoning Terminal). Retries are also cut to 0 since the
      // endpoint has its own multi-tier fallback (L1/L2/L3).
      const data = await orchestrator.request({
        key: `decision:${sym}`,
        url: `/decision/${sym}?with_ai=true`,
        priority: 'HIGH',
        ttl: 5000,
        timeout: 20000,
        retries: 0,
      });
      if (data && (data.analysis || data.final_signal)) {
        setDecision(data);
        return;
      }
    } catch (e) {
      // Phase 51.M+ — do NOT nullify decision on transient error. The
      // last-known reading stays visible; a small toast informs the
      // operator that this refresh failed but earlier data is still on
      // screen. Prevents the "all three panels went dark" bug.
      toast.error(e?.response?.data?.detail || 'Decision refresh failed — falling back to fast tier');
    } finally {
      setLoadingDec(false);
    }
    // ── Fallback path: if the AI-included fetch failed or returned an
    // empty shell, try the fast (no-AI) variant so the numbers /
    // firewall panel still render. The reasoning terminal will just
    // show "// awaiting briefing..." until the next successful pull.
    try {
      const fastData = await orchestrator.request({
        key: `decision:fast:${sym}`,
        url: `/decision/${sym}?with_ai=false`,
        priority: 'HIGH',
        ttl: 5000,
        timeout: 8000,
        retries: 0,
      });
      if (fastData && (fastData.analysis || fastData.final_signal)) {
        setDecision((prev) => ({ ...(prev || {}), ...fastData }));
      }
    } catch {
      // Silent — we've already surfaced the primary error via toast.
    }
  };

  const loadLogs = async () => {
    try {
      const { data } = await api.get('/system/logs?limit=25');
      setEvents(data.events || []);
    } catch {}
  };

  const refreshAll = async () => {
    const syms = await loadWatchlistSymbols();
    // Force-invalidate the decision cache so the user gets a true refresh.
    orchestrator.invalidate(`decision:${symbol}`);
    await Promise.all([refreshQuotes(syms, true), loadDecision(symbol), loadLogs()]);
    setLastRefresh(new Date().toISOString());
  };

  // Tier 1: quotes every 15s (slower → readable, no flicker). Only the
  // quotes refresh — the symbol list is stable between explicit edits.
  useVisibilityAwareInterval(() => {
    refreshQuotes(watchlist, false);
    loadWatchSnapshot();      // Phase 36.A — refresh canonical snapshot
    loadTradingHealth();      // Phase 36.D — refresh trading health pill
    setLastRefresh(new Date().toISOString());
  }, 15000, [watchlist]);
  // Tier 3: system log every 20s
  useVisibilityAwareInterval(loadLogs, 20000, []);

  // Push-assist: instantly refresh when backend emits watchlist.snapshot event.
  useEventStream({
    'watchlist.snapshot': (payload) => {
      // Phase 36.5.B — Reconcile via canonical store so SSE updates merge
      // without ever blanking known-good fields.
      if (payload?.rows || payload?.symbol) watchlistStore.reconcileSSE(payload);
      else watchlistStore.refresh();
      refreshQuotes(watchlist, false);
      setLastRefresh(new Date().toISOString());
    },
  });

  useEffect(() => { loadDecision(symbol); }, [symbol]);

  // Phase 36.5.B — Subscribe to the canonical watchlist store. The store
  // persists across mount/unmount; tab switches never blank a hydrated row.
  useEffect(() => {
    const unsub = watchlistStore.subscribe((snap) => {
      setWatchSnapshot({ rows: snap.rows, bySymbol: snap.bySymbol, quotes: snap.quotes });
    });
    return unsub;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const syms = await loadWatchlistSymbols();
        await refreshQuotes(syms, true);
        setLastRefresh(new Date().toISOString());
      } catch (e) {
        console.warn('initial watchlist load failed:', e?.message);
      }
      try { await loadLogs(); } catch {}
      // Phase 36 — initial loads of canonical health pills
      loadWatchSnapshot();
      loadTradingHealth();
    })();
  }, []);

  // Phase 29.2 §B — Symbol normalization + immediate hydration with retry
  const normalizeSymbol = (s) => (s || '').toString().toUpperCase().trim().replace(/[^A-Z0-9.\-]/g, '');

  const hydrateSymbol = async (sym) => {
    // Mark pending, show Loading state in UI
    setPendingHydration((p) => ({ ...p, [sym]: Date.now() }));
    const MAX_ATTEMPTS = 4;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        // Bypass cache on first 2 attempts (force=true) — priority WATCHLIST_CRITICAL
        const force = attempt <= 2 ? '&force=true' : '';
        const qRes = await api.get(`/market/batch?symbols=${sym}${force}`);
        const q = qRes.data?.quotes?.[sym];
        if (q && q.c != null && q.c > 0) {
          setQuotes((prev) => ({ ...prev, [sym]: q }));
          lastKnownQuotesRef.current[sym] = { c: q.c, dp: q.dp };
          watchlistStore.setQuote(sym, q);
          setPendingHydration((p) => { const n = { ...p }; delete n[sym]; return n; });
          return true;
        }
      } catch {}
      // Exponential-ish backoff: 250ms, 500ms, 1s, 2s
      await new Promise((r) => setTimeout(r, 250 * Math.pow(2, attempt - 1)));
    }
    // Failed all retries — leave pending TRUE so UI shows "unavailable" not "—"
    setPendingHydration((p) => ({ ...p, [sym]: -1 })); // -1 = failed
    return false;
  };

  const addToWatchlist = async (s) => {
    const sym = normalizeSymbol(s || input);
    if (!sym) { toast.error('Invalid symbol'); return; }
    try {
      const { data } = await api.post('/watchlist/add', { symbol: sym });
      setWatchlist(data.symbols);
      watchlistStore.setSymbols(data.symbols || []);
      toast.success(`Added ${sym} to watchlist`);
      // Phase 36.5.B hardening — three-pronged hydrate so the new symbol
      // appears with a real price BEFORE any tab switch / reload:
      //   1) Force a quote (writes to store.quotes via setQuote)
      //   2) Refresh the watchlist snapshot (writes to store.bySymbol)
      //   3) Broadcast list refresh
      await Promise.all([
        hydrateSymbol(sym),
        watchlistStore.refresh(),
        refreshQuotes(data.symbols, true),
      ]);
    } catch (e) {
      toast.error('Add failed');
    }
  };
  const removeFromWatchlist = async (s) => {
    const { data } = await api.post('/watchlist/remove', { symbol: s });
    setWatchlist(data.symbols);
    watchlistStore.setSymbols(data.symbols || []);
    watchlistStore.removeSymbol(s);
  };

  const search = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setSymbol(input.trim().toUpperCase());
  };

  const a = decision?.analysis;
  const ind = a?.indicators || {};
  const ss = a?.sub_scores || {};
  const setup = a?.setup || {};
  const nt = decision?.no_trade;
  const dfresh = decision?.freshness;
  const validation = decision?.validation;

  const regimeTrend = a?.regime?.trend || '—';
  const regimeVol = a?.regime?.volatility || '—';

  const freshColor = (s) => s === 'fresh' ? 'text-buy' : s === 'caution' ? 'text-cyan-sys' : s === 'delayed' ? 'text-hold' : 'text-sell';
  const fMap = Object.fromEntries((freshness.rows || []).map((r) => [r.symbol, r]));

  return (
    <div className="p-4 space-y-3">
      {freshness.any_stale && (
        <div data-testid="stale-banner" className="panel border-sell bg-[rgba(255,0,60,0.06)] no-trade-glow px-4 py-2 text-[12px] font-mono text-sell flex items-center gap-3">
          <span className="font-display tracking-[0.3em] uppercase text-[11px]">STALE MODE</span>
          <span className="text-[var(--txt-primary)]">
            {freshness.stale_count} symbol(s) have data older than 5 minutes — signals auto-suppressed.
          </span>
        </div>
      )}
      {/* Phase 52.1 Next+ — Multi-symbol regime strip. Only surface for
          crypto symbols (BTCUSDT / ETHUSDT / … suffix or contained in
          the crypto universe). Everything else is silently skipped by
          the /crypto/regime endpoint (returns unknown regime). */}
      {watchlist?.length > 0 && (
        <RegimeGrid
          symbols={watchlist.filter((s) => /USDT?$|USDC$|BTC$|ETH$/.test(s))}
        />
      )}
      {/* Phase 36.D — Trading Engine Health pill */}
      {tradingHealth && (
        <div data-testid="trading-health-pill"
             className="panel px-3 py-1.5 text-[10px] font-mono flex items-center gap-4 flex-wrap">
          <span className={`font-display tracking-[0.3em] uppercase ${
            tradingHealth.state === 'HEALTHY'    ? 'text-buy'    :
            tradingHealth.state === 'DEGRADED'   ? 'text-hold'   :
            tradingHealth.state === 'RECOVERING' ? 'text-cyan-sys' :
            'text-sell'
          }`}>◉ trading engine · {tradingHealth.state}</span>
          <span className="text-[var(--txt-muted)]">
            decisions {tradingHealth.decisions_served}
            {tradingHealth.decisions_errors > 0 && <span className="text-sell"> · {tradingHealth.decisions_errors} err</span>}
          </span>
          {tradingHealth.quant_exec_ms_ema != null && (
            <span className="text-[var(--txt-muted)]">quant {tradingHealth.quant_exec_ms_ema}ms</span>
          )}
          {tradingHealth.hydrate_success_rate != null && (
            <span className="text-[var(--txt-muted)]">hyd {Math.round(tradingHealth.hydrate_success_rate * 100)}%</span>
          )}
          <span className="text-[var(--txt-muted)] flex items-center gap-1">
            providers:
            {(tradingHealth.providers || []).map((p) => (
              <span key={p.name} className={
                p.state === 'GREEN'  ? 'text-buy'  :
                p.state === 'YELLOW' ? 'text-hold' :
                p.state === 'RED'    ? 'text-sell' : ''
              } data-testid={`trading-health-provider-${p.name}`}>{p.name}:{p.score ?? '—'}</span>
            ))}
          </span>
        </div>
      )}
      {/* TOP BAR: No-Trade + Search + Refresh */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_320px_auto] gap-3">
        <NoTradeBanner noTrade={nt?.no_trade} reason={nt?.reason} failed={nt?.failed_count} />
        <form onSubmit={search} className="panel flex items-center px-3" data-testid="symbol-search-form">
          <Search size={14} className="text-cyan-sys mr-2" />
          <input
            data-testid="symbol-search-input"
            value={input}
            onChange={(e) => setInput(e.target.value.toUpperCase())}
            placeholder="SCAN TARGET (e.g. NVDA)"
            className="flex-1 bg-transparent outline-none py-2 font-mono text-[13px] text-cyan-sys"
          />
          <button
            type="submit"
            data-testid="symbol-search-submit"
            className="text-[10px] font-display tracking-[0.3em] border border-cyan-sys text-cyan-sys px-2 py-1 uppercase hover:bg-cyan-sys hover:text-black"
          >
            engage
          </button>
        </form>
        <RefreshButton onRefresh={refreshAll} lastUpdated={lastRefresh} testId="dashboard-refresh" />
      </div>

      {/* MAIN GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        {/* WATCHLIST */}
        <CornerPanel
          title={`Watchlist // ${watchlist.length}`}
          className="lg:col-span-3"
          testId="watchlist-panel"
          right={<PanelStatus lastUpdated={lastRefresh} testId="watchlist-status" />}
        >
          <div className="flex gap-1 mb-2">
            <input
              data-testid="watchlist-add-input"
              value={input}
              onChange={(e) => setInput(e.target.value.toUpperCase())}
              className="flex-1 bg-black border border-[var(--border-muted)] focus:border-cyan-sys outline-none px-2 py-1 font-mono text-[12px]"
              placeholder="SYM"
            />
            <button
              data-testid="watchlist-add-btn"
              onClick={() => addToWatchlist()}
              className="text-[10px] font-display tracking-[0.25em] uppercase border border-cyan-sys text-cyan-sys px-2 hover:bg-cyan-sys hover:text-black"
            >
              + add
            </button>
          </div>
          <div className="divide-y divide-[var(--border-default)] max-h-[360px] overflow-y-auto pr-1 atde-scroll" data-testid="watchlist-scroll">
            {watchlist.length === 0 && <div className="text-[var(--txt-muted)] text-[12px] py-2">// empty</div>}
            {watchlist.map((s) => {
              // Phase 36.A / 36.5.B — Bind to canonical snapshot store (last-good preserved).
              const snap = watchSnapshot?.bySymbol?.[s] || (watchSnapshot?.rows || []).find((r) => r.symbol === s);
              // Phase 36.5.B — module-scoped quote cache survives Dashboard remounts.
              const storedQ = watchSnapshot?.quotes?.[s] || watchlistStore.getQuote(s);
              const q = quotes[s] || storedQ;
              // Prefer snapshot price (server-side last-good guaranteed); fall back to live quote.
              const snapshotPrice = typeof snap?.price === 'number' ? snap.price : null;
              const displayPrice = q?.c ?? snapshotPrice ?? lastKnownQuotesRef.current[s]?.c ?? storedQ?.c ?? null;
              const displayPct = q?.dp ?? lastKnownQuotesRef.current[s]?.dp ?? storedQ?.dp ?? null;
              if (q?.c != null) lastKnownQuotesRef.current[s] = { c: q.c, dp: q.dp };
              const up = (displayPct || 0) >= 0;
              // Phase 29.2 §B — pending hydration state
              const pending = pendingHydration[s];
              const isHydrating = pending && pending > 0;
              const hydrationFailed = pending === -1;
              const showPrice = displayPrice != null
                ? displayPrice.toFixed(2)
                : isHydrating ? 'Loading...' : hydrationFailed ? 'unavailable' : '—';
              const showPct = displayPct != null
                ? formatPct(displayPct)
                : isHydrating ? '' : '';
              const companyName = snap?.company_name && snap.company_name !== s ? snap.company_name : null;
              // Phase 36.A — server-derived state pill (HEALTHY/DEGRADED/STALE/RECOVERING)
              const snapState = snap?.state;
              const stateClass =
                snapState === 'HEALTHY'    ? 'text-buy' :
                snapState === 'DEGRADED'   ? 'text-hold' :
                snapState === 'RECOVERING' ? 'text-cyan-sys' :
                snapState === 'STALE'      ? 'text-sell' : '';
              return (
                <div
                  key={s}
                  data-testid={`watchlist-row-${s}`}
                  className={`flex items-center justify-between py-2 cursor-pointer hover:bg-[#0d1113] ${symbol === s ? 'bg-[#0d1416]' : ''}`}
                  onClick={() => setSymbol(s)}
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Star size={12} className={symbol === s ? 'text-cyan-sys' : 'text-[var(--txt-muted)]'} />
                    <div className="min-w-0">
                      <div className="font-mono text-[13px] leading-tight">{s}</div>
                      {/* Phase 36.B — Company name shown below ticker. Never blocks render. */}
                      {companyName && (
                        <div data-testid={`watchlist-company-${s}`}
                             className="text-[10px] text-[var(--txt-muted)] truncate leading-tight">
                          {companyName}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {snap?.degraded_reason === 'upstream_throttled' && (
                      <span
                        data-testid={`watchlist-throttled-${s}`}
                        title={`Upstream API throttled — showing cached value from ${snap.upstream_last_good_ts ? new Date(snap.upstream_last_good_ts * 1000).toLocaleTimeString() : 'earlier'}`}
                        className="text-[9px] tracking-widest uppercase px-1.5 py-0.5 border border-hold text-hold bg-hold/10 font-mono"
                      >
                        throttled
                      </span>
                    )}
                    {snapState && (
                      <span data-testid={`watchlist-state-${s}`}
                            className={`text-[8px] uppercase tracking-widest ${stateClass}`}
                            title={snap?.degraded_reason || snapState}>
                        ◉
                      </span>
                    )}
                    <span
                      data-testid={`watchlist-price-${s}`}
                      className={`font-mono text-[12px] tabular live-num ${isHydrating ? 'text-[var(--txt-muted)] italic' : hydrationFailed ? 'text-sell' : ''}`}
                    >{showPrice}</span>
                    <span className={`font-mono text-[11px] tabular live-num ${up ? 'text-buy' : 'text-sell'}`}>
                      {showPct}
                    </span>
                    {fMap[s] && fMap[s].age_seconds !== null && (
                      <span
                        title={`last updated ${fMap[s].age_seconds}s ago`}
                        className={`text-[9px] tracking-widest uppercase live-num ${freshColor(fMap[s].state)}`}
                      >
                        {fMap[s].age_seconds}s
                      </span>
                    )}
                    <button
                      aria-label="remove"
                      onClick={(e) => { e.stopPropagation(); removeFromWatchlist(s); }}
                      className="text-[var(--txt-muted)] hover:text-sell"
                      data-testid={`watchlist-remove-${s}`}
                    >
                      <X size={12} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </CornerPanel>

        {/* LIVE CHART */}
        <CornerPanel
          title={`Chart // ${symbol}`}
          right={<span className="text-cyan-sys">TradingView Feed</span>}
          className="lg:col-span-9"
          testId="chart-panel"
        >
          <TradingViewWidget symbol={symbol} height={380} />
        </CornerPanel>

        {/* AI DECISION */}
        <CornerPanel
          title={`AI Decision // ${symbol}`}
          right={
            <button
              data-testid="refresh-decision"
              onClick={() => loadDecision(symbol)}
              className="flex items-center gap-1 text-[10px] font-display tracking-[0.2em] uppercase text-cyan-sys hover:text-white"
            >
              <RefreshCw size={10} className={loadingDec ? 'animate-spin' : ''} /> refresh
            </button>
          }
          className="lg:col-span-5"
          active
          testId="decision-panel"
        >
          {!decision && !loadingDec && <div className="text-[var(--txt-muted)]">// awaiting analysis...</div>}
          {loadingDec && <div className="text-cyan-sys">// computing confluence...</div>}
          {decision && a && (
            <div className="grid grid-cols-1 sm:grid-cols-[210px_1fr] gap-4 items-center">
              <ConfluenceGauge
                value={a.confluence}
                signal={decision.final_signal}
              />
              <div className="space-y-2 text-[12px] font-mono">
                <div className="flex items-center gap-3">
                  <SignalPill signal={decision.final_signal} testId="decision-signal" />
                  <span className="text-[var(--txt-secondary)]">
                    raw: <span className="text-[var(--txt-primary)]">{a.signal}</span>
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                  <span className="text-[var(--txt-secondary)]">PRICE</span><span>{a.symbol_close}</span>
                  <span className="text-[var(--txt-secondary)]">REGIME</span><span className="text-cyan-sys">{regimeTrend} / {regimeVol}</span>
                  <span className="text-[var(--txt-secondary)]">RSI14</span><span>{ind.rsi14 ?? '—'}</span>
                  <span className="text-[var(--txt-secondary)]">MACD H</span><span>{ind.macd_hist ?? '—'}</span>
                  <span className="text-[var(--txt-secondary)]">VOL x</span><span>{ind.vol_ratio ?? '—'}</span>
                  <span className="text-[var(--txt-secondary)]">ATR%</span><span>{ind.atr_pct ?? '—'}</span>
                </div>
                <div className="border-t border-[var(--border-default)] pt-2 mt-2 grid grid-cols-4 gap-2 text-center">
                  <div><div className="text-[10px] text-[var(--txt-secondary)]">ENTRY</div><div>{setup.entry}</div></div>
                  <div><div className="text-[10px] text-[var(--txt-secondary)]">STOP</div><div className="text-sell">{setup.stop}</div></div>
                  <div><div className="text-[10px] text-[var(--txt-secondary)]">TARGET</div><div className="text-buy">{setup.target}</div></div>
                  <div><div className="text-[10px] text-[var(--txt-secondary)]">R:R</div><div className={setup.r_r >= 2 ? 'text-buy' : 'text-hold'}>{setup.r_r}</div></div>
                </div>
                {decision.spread && decision.spread.bid != null && (
                  <div
                    data-testid="decision-spread"
                    className="border-t border-[var(--border-default)] pt-2 mt-2 flex items-center justify-between text-[11px]"
                  >
                    <span className="text-[var(--txt-secondary)] uppercase tracking-wider text-[10px]">Est. Spread</span>
                    <span className="font-mono">
                      <span className="text-sell">{decision.spread.bid}</span>
                      <span className="text-[var(--txt-muted)] mx-1">·</span>
                      <span className="text-cyan-sys">{decision.spread.mid}</span>
                      <span className="text-[var(--txt-muted)] mx-1">·</span>
                      <span className="text-buy">{decision.spread.ask}</span>
                      <span className="ml-2 text-[var(--txt-secondary)] text-[10px]">
                        {decision.spread.spread_bps} bps
                      </span>
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </CornerPanel>

        {/* NO-TRADE CHECKLIST + SIGNAL VALIDATION */}
        <CornerPanel title="Firewall + Validation" className="lg:col-span-3" testId="firewall-panel">
          {nt?.checks?.map((c, i) => (
            <div key={i} className="flex items-center justify-between text-[12px] font-mono py-1 border-b border-[var(--border-default)] last:border-none">
              <span className="truncate text-[var(--txt-primary)]">{c.check}</span>
              <span className={c.pass ? 'text-buy' : 'text-sell'}>
                {c.pass ? 'PASS' : 'FAIL'}
              </span>
            </div>
          )) || <div className="text-[var(--txt-muted)]">// no data</div>}
          {validation && (
            <div className="mt-3 pt-2 border-t border-[var(--border-default)] text-[11px] font-mono">
              <div className="flex justify-between">
                <span className="text-[var(--txt-secondary)] uppercase tracking-wider text-[10px]">validation</span>
                <span className={validation.verdict === 'pass' ? 'text-buy' : validation.verdict === 'downgrade' ? 'text-hold' : 'text-sell'}>
                  {validation.verdict}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--txt-secondary)] uppercase tracking-wider text-[10px]">mtf</span>
                <span>{validation.mtf?.dirs ? `S${validation.mtf.dirs.short}/M${validation.mtf.dirs.medium}/L${validation.mtf.dirs.long}` : '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--txt-secondary)] uppercase tracking-wider text-[10px]">breakout</span>
                <span className={validation.breakout?.type === 'fakeout' ? 'text-sell' : validation.breakout?.type === 'breakout' ? 'text-buy' : ''}>
                  {validation.breakout?.type || '—'}
                </span>
              </div>
              {dfresh && (
                <div className="flex justify-between">
                  <span className="text-[var(--txt-secondary)] uppercase tracking-wider text-[10px]">data age</span>
                  <span className={freshColor(dfresh.state)}>
                    {dfresh.age_seconds !== null ? `${dfresh.age_seconds}s · ${dfresh.state}` : '—'}
                  </span>
                </div>
              )}
            </div>
          )}
        </CornerPanel>

        {/* AI REASONING TERMINAL */}
        <CornerPanel
          title="AI Reasoning Terminal"
          right={<span className="text-cyan-sys">Claude Sonnet 4.5</span>}
          className="lg:col-span-4"
          testId="reasoning-panel"
        >
          <div className="scanlines bg-black border border-[var(--border-default)] h-[260px] overflow-y-auto p-3 font-mono text-[12px] leading-[1.6] text-buy whitespace-pre-wrap">
            {decision?.reasoning || '// awaiting briefing...'}
            <span className="blink">_</span>
          </div>
        </CornerPanel>

        {/* P11 SIGNAL QUALITY PANEL */}
        <CornerPanel
          title="Signal Quality // P11 Intelligence Layer"
          right={decision?._served_from ? (
            <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--txt-secondary)]">
              L{decision._layer ?? '?'} · {decision._served_from}
            </span>
          ) : null}
          className="lg:col-span-12"
          testId="signal-quality-panel"
        >
          {decision?.signal_quality ? (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-[12px] font-mono">
              <QualityMetric
                label="Strength"
                value={decision.signal_quality.strength}
                testId="sq-strength"
              />
              <QualityMetric
                label="Historical WR"
                value={decision.signal_quality.historical_accuracy}
                suffix="%"
                testId="sq-historical"
              />
              <QualityMetric
                label="Noise Filter"
                value={decision.signal_quality.noise_score}
                testId="sq-noise"
              />
              <QualityMetric
                label="Stability"
                value={decision.signal_quality.stability_score}
                testId="sq-stability"
              />
              <div data-testid="sq-calibration" className="space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-cyan-sys">Calibrated Confidence</div>
                <div className="flex items-baseline gap-2">
                  <span className={`text-2xl ${
                    (decision.signal_quality.calibration?.calibrated ?? 0) >= 70 ? 'text-buy' :
                    (decision.signal_quality.calibration?.calibrated ?? 0) >= 50 ? 'text-hold' : 'text-sell'
                  }`}>
                    {decision.signal_quality.calibration?.calibrated ?? '—'}
                  </span>
                  <span className="text-[11px] text-[var(--txt-muted)]">
                    raw {decision.signal_quality.calibration?.raw ?? '—'}
                  </span>
                  {decision.signal_quality.calibration?.applied && (
                    <span className={`text-[10px] px-1 border ${
                      (decision.signal_quality.calibration?.drift ?? 0) >= 0
                        ? 'text-buy border-buy' : 'text-sell border-sell'
                    }`}>
                      {(decision.signal_quality.calibration?.drift ?? 0) >= 0 ? '+' : ''}
                      {decision.signal_quality.calibration?.drift}
                    </span>
                  )}
                </div>
                <div className="text-[9px] text-[var(--txt-muted)] uppercase tracking-wider">
                  n={decision.signal_quality.calibration?.sample_size ?? 0}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-[var(--txt-muted)] text-[12px]">// quality telemetry initializing...</div>
          )}
          {/* P12: Exit Risk sub-section — only when the symbol has an open position */}
          {decision?.entry_zone?.zone && (
            <div data-testid="entry-zone-block" className="mt-4 pt-3 border-t border-[var(--border-default)]">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-cyan-sys mb-1">Entry Zone</div>
                  <div className="flex items-baseline gap-2 font-mono">
                    <span className="text-[var(--txt-muted)]">${decision.entry_zone.zone.low.toFixed(2)}</span>
                    <span className="text-cyan-sys">→</span>
                    <span className="text-2xl text-buy">${decision.entry_zone.zone.mid.toFixed(2)}</span>
                    <span className="text-cyan-sys">→</span>
                    <span className="text-[var(--txt-muted)]">${decision.entry_zone.zone.high.toFixed(2)}</span>
                  </div>
                  <div className="text-[9px] text-[var(--txt-muted)] uppercase tracking-wider mt-1">
                    width {decision.entry_zone.zone.width_atr}× ATR · current ${decision.entry_zone.current_price}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <span className={`text-[11px] tracking-[0.3em] uppercase px-2 py-1 border ${
                    decision.entry_zone.timing === 'SAFE_ENTRY' ? 'text-buy border-buy' :
                    decision.entry_zone.timing === 'CHASING'    ? 'text-hold border-hold' :
                                                                  'text-sell border-sell'
                  }`} data-testid="entry-zone-timing">
                    {decision.entry_zone.timing.replace('_', ' ')}
                  </span>
                  <span className="text-[10px] text-[var(--txt-secondary)] italic max-w-[260px]">
                    {decision.entry_zone.timing_reason}
                  </span>
                </div>
              </div>
            </div>
          )}
          {decision?.exit_risk && (
            <div data-testid="exit-risk-block" className="mt-4 pt-3 border-t border-[var(--border-default)] flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-cyan-sys mb-1">Exit Risk // open position</div>
                <div className="flex items-baseline gap-3">
                  <span className={`text-3xl live-num ${
                    decision.exit_risk.status === 'EXIT_EARLY' ? 'text-sell' :
                    decision.exit_risk.status === 'HOLD_CAUTIOUS' ? 'text-hold' : 'text-buy'
                  }`}>
                    {decision.exit_risk.exit_risk_score}
                  </span>
                  <span className={`text-[11px] tracking-[0.3em] uppercase px-2 py-0.5 border ${
                    decision.exit_risk.status === 'EXIT_EARLY' ? 'text-sell border-sell' :
                    decision.exit_risk.status === 'HOLD_CAUTIOUS' ? 'text-hold border-hold' : 'text-buy border-buy'
                  }`}>
                    {decision.exit_risk.status.replace('_', ' ')}
                  </span>
                </div>
              </div>
              <div className="flex-1 text-[11px] font-mono text-[var(--txt-secondary)]">
                {(decision.exit_risk.reasons || []).length === 0 ? (
                  <span className="text-[var(--txt-muted)]">// no deterioration flags</span>
                ) : (
                  <ul className="space-y-0.5">
                    {(decision.exit_risk.reasons || []).slice(0, 4).map((r, i) => (
                      <li key={i}>▸ {r}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </CornerPanel>

        {/* SYSTEM LOG */}
        <CornerPanel title="System Feed" className="lg:col-span-12" testId="system-feed">
          <TerminalLog events={events} height={200} />
        </CornerPanel>
      </div>
    </div>
  );
}
